import { useTranslation } from "react-i18next";

import { Toggle } from "@/components/Toggle/Toggle";
import { classNames } from "@/utils/classNames";

import { CompanySelection, companyToggleCopy } from "./companyBlacklist";

interface CompanyBlacklistToggleProps {
	/** The companies the selection would block; `null` when they are not known up front. */
	selection: CompanySelection;
	isChecked: boolean;
	onChange: (value: boolean) => void;
}

/**
 * "Also blacklist the company", naming the company it blocks. The lead's company is their current
 * employer, which need not be the company a list was built for, so the toggle says which one it is
 * rather than "the entire company" (PREC-4425). It cannot be switched on for leads with no company.
 */
export const CompanyBlacklistToggle = ({
	selection,
	isChecked,
	onChange,
}: CompanyBlacklistToggleProps) => {
	const { t } = useTranslation();
	const copy = companyToggleCopy(selection);

	return (
		<div className="mt-4" data-test="company-blacklist-toggle">
			<div className="flex flex-row items-center">
				<Toggle
					disabled={copy.disabled}
					isChecked={isChecked && !copy.disabled}
					testId="company-blacklist-switch"
					onChange={() => onChange(!isChecked)}
				/>
				<button
					className={classNames(
						"ml-2 text-left text-ssm text-gray-700",
						copy.disabled ? "cursor-not-allowed" : "cursor-pointer"
					)}
					disabled={copy.disabled}
					type="button"
					onClick={() => onChange(!isChecked)}
				>
					{t(`blacklist.companyToggle.${copy.key}`, copy.options)}
				</button>
			</div>
			{copy.hintKey && (
				<p className="ml-[46px] mt-1 text-xs text-gray-500">
					{t(`blacklist.companyToggle.${copy.hintKey}`)}
				</p>
			)}
		</div>
	);
};
